import { useState } from "react";
import { Box, Button, Grid, IconButton, Typography } from "@mui/material";
import {
  Close as CloseIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";
import { NoPermission } from "~/components/no-permission";
import { ResourceInvitations } from "~/components/Members/ResourceInvitations";
import { ResourceMembers } from "~/components/Members/ResourceMembers";
import { AddMemberDialog } from "./AddMemberDialog";
import { useScope } from "~/hooks/useScope";
import { useCanListOrganizationMemberships } from "~/lib/iam/actions/mondoo/captain/Captain/ListOrganizationMemberships";
import { useCanListSpaceMemberships } from "~/lib/iam/actions/mondoo/captain/Captain/ListSpaceMemberships";
import { useCanCreate } from "~/lib/iam/actions/mondoo/invitations/InvitationManager/Create";

type MembersMgmtProps = {};

type MembersMgmtState = {
  addMemberOpen: boolean;
};

export function MembersMgmt({}: MembersMgmtProps) {
  const [showAlert, setShowAlert] = useState<boolean>(true);
  const [state, setState] = useState<MembersMgmtState>({
    addMemberOpen: false,
  });

  // We need to get org from this hook because if this component rendered in Space setting, org is undefined
  const { activeScope, organizationScope, spaceScope } = useScope();

  const mergeState = (params: Partial<MembersMgmtState>) => {
    return setState((prevState) => ({
      ...prevState,
      ...params,
    }));
  };

  const handleCloseInfoAlert = () => {
    setShowAlert(false);
  };

  const hasListOrgMembersPermission =
    useCanListOrganizationMemberships(activeScope);

  const hasListSpaceMembersPermission = useCanListSpaceMemberships(activeScope);

  const hasAddPermission = useCanCreate(activeScope);

  if (
    (activeScope?.type === "space" && !hasListSpaceMembersPermission) ||
    (activeScope?.type === "organization" && !hasListOrgMembersPermission)
  ) {
    return <NoPermission />;
  }

  const mrn = activeScope?.mrn;

  return (
    <>
      {mrn && (
        <AddMemberDialog
          resourceMrn={mrn}
          onClose={() => mergeState({ addMemberOpen: false })}
          dialogProps={{
            fullWidth: true,
            open: state.addMemberOpen,
          }}
        />
      )}
      <Grid container spacing={3}>
        {hasAddPermission && spaceScope && showAlert && (
          <Grid size={12}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "background.paper",
                flexDirection: { xs: "column", md: "row" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  flex: 1,
                  paddingTop: 0.5,
                  mb: { xs: 2, md: 0 },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    border: "1px solid",
                    borderColor: "info.main",
                    color: "info.main",
                    mr: 2,
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                  }}
                >
                  <InfoOutlinedIcon />
                </Box>
                <Typography variant="body2" sx={{ color: "info.main" }}>
                  You can add members to your entire organization or individual
                  spaces. Add members to your space here. Manage
                  organization-wide permissions from the Organization's Settings
                  page.
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleCloseInfoAlert}
                  sx={{
                    ml: 2,
                    display: { xs: "block", md: "none" },
                    height: "30px",
                  }}
                >
                  <CloseIcon fontSize="small" color="info" />
                </IconButton>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  ml: { sm: 0, md: 2 },
                  mt: 0,
                  alignSelf: { xs: "flex-end", md: "flex-start" },
                }}
              >
                <Button
                  variant="text"
                  color="primary"
                  size="small"
                  component="a"
                  href={`/organization/settings/members?${organizationScope?.params}`}
                  sx={{
                    mr: 1,
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                  }}
                >
                  View Organization Settings
                </Button>
                <IconButton
                  size="small"
                  onClick={handleCloseInfoAlert}
                  sx={{ display: { xs: "none", md: "block" }, height: "30px" }}
                >
                  <CloseIcon fontSize="small" color="info" />
                </IconButton>
              </Box>
            </Box>
          </Grid>
        )}

        <ResourceInvitations mrn={mrn!} />

        <ResourceMembers
          handleAddMemberClick={
            hasAddPermission
              ? () => mergeState({ addMemberOpen: true })
              : undefined
          }
        />
      </Grid>
    </>
  );
}
